/**
 * Tipo Appointments availability banner for sage-theme.
 *
 * Renders a live per-session availability banner on the reservation product
 * page by querying the same Tipo slots API that the Tipo widget uses.
 * Activates only for products with a [data-tipo-slot-banner] element; scope
 * is controlled at the Liquid level (sections/product.liquid) via the
 * suppress_checkout_product_ids allowlist.
 *
 * Companion to tipo-booking-overrides.js. See
 * TIPO_BOOKING_TROUBLESHOOTING_RUNBOOK.md for the canonical API surface.
 */

(function () {
  const banner = document.querySelector("[data-tipo-slot-banner]");
  if (!banner) return;

  const productId = banner.getAttribute("data-tipo-product-id");
  const variantId = banner.getAttribute("data-tipo-variant-id") || "";
  if (!productId) return;

  const list = banner.querySelector("[data-tipo-slot-list]");
  if (!list) return;

  const LIMITED_THRESHOLD = 5;
  const REFRESH_INTERVAL_MS = 60_000;
  const MAX_TIPO_WAIT_ATTEMPTS = 40; // 40 × 250ms = 10s
  let rawResponseLogged = false;

  function daysFromNow(days) {
    const d = new Date(Date.now() + days * 86_400_000);
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  function resolveTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    } catch (_e) {
      return "America/New_York";
    }
  }

  async function waitForTipoAppUrl() {
    for (let i = 0; i < MAX_TIPO_WAIT_ATTEMPTS; i++) {
      const appUrl = window.Tipo && window.Tipo.Booking && window.Tipo.Booking.appUrl;
      if (appUrl) {
        try {
          return new URL(appUrl, window.location.origin).origin;
        } catch (_e) {
          return null;
        }
      }
      await new Promise((r) => setTimeout(r, 250));
    }
    return null;
  }

  async function fetchSlots() {
    const origin = await waitForTipoAppUrl();
    if (!origin) return null;

    const url = new URL("/api/front/slots", origin);
    url.searchParams.set("productId", productId);
    if (variantId) url.searchParams.set("variantId", variantId);
    url.searchParams.set("employeeId", "0");
    url.searchParams.set("bringQty", "0");
    url.searchParams.set("timezone", resolveTimezone());
    url.searchParams.set("fromDate", daysFromNow(0));
    url.searchParams.set("toDate", daysFromNow(120));

    try {
      const res = await fetch(url.toString(), { credentials: "omit" });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn("[tipo-banner] slot fetch failed", err);
      return null;
    }
  }

  // Defensive parser — tolerates several plausible Tipo response shapes.
  function extractSlotArray(data) {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== "object") return [];
    return (
      data.slots ||
      data.data ||
      data.availability ||
      data.items ||
      data.results ||
      []
    );
  }

  function extractStart(slot) {
    const raw =
      slot.start_time ||
      slot.startTime ||
      slot.start ||
      slot.sessionStart ||
      slot.datetime ||
      slot.dateTime ||
      (slot.date && slot.time ? slot.date + "T" + slot.time : null) ||
      slot.date;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  function extractLeft(slot) {
    if (typeof slot.left === "number") return slot.left;
    if (typeof slot.remaining === "number") return slot.remaining;
    if (typeof slot.available === "number") return slot.available;
    if (typeof slot.availableSpots === "number") return slot.availableSpots;
    if (typeof slot.spots_left === "number") return slot.spots_left;
    if (typeof slot.seats_left === "number") return slot.seats_left;
    if (typeof slot.capacity === "number" && typeof slot.booked === "number") {
      return Math.max(0, slot.capacity - slot.booked);
    }
    return null;
  }

  function parseSlots(data) {
    const pastCutoff = Date.now() - 86_400_000; // tolerate same-day slots
    return extractSlotArray(data)
      .map((s) => ({ start: extractStart(s), left: extractLeft(s) }))
      .filter((s) => s.start && s.start.getTime() > pastCutoff)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  function formatSlotLabel(date) {
    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return dateStr + " · " + timeStr;
  }

  function buildItem(slot) {
    const li = document.createElement("li");
    li.className = "tipo-slot-banner__item";

    const labelEl = document.createElement("span");
    labelEl.className = "tipo-slot-banner__label";
    labelEl.textContent = formatSlotLabel(slot.start);

    const countEl = document.createElement("span");
    countEl.className = "tipo-slot-banner__count";

    if (slot.left === null) {
      countEl.textContent = "Check availability";
    } else if (slot.left <= 0) {
      countEl.textContent = "Sold out";
      countEl.classList.add("tipo-slot-banner__count--soldout");
      li.classList.add("tipo-slot-banner__item--soldout");
    } else if (slot.left <= LIMITED_THRESHOLD) {
      countEl.textContent = slot.left + " seats left · Limited";
      countEl.classList.add("tipo-slot-banner__count--limited");
    } else {
      countEl.textContent = slot.left + " seats left";
    }

    li.appendChild(labelEl);
    li.appendChild(countEl);
    return li;
  }

  function render(slots) {
    if (!slots.length) {
      banner.hidden = true;
      return;
    }
    const frag = document.createDocumentFragment();
    slots.forEach((slot) => frag.appendChild(buildItem(slot)));
    list.innerHTML = "";
    list.appendChild(frag);
    banner.hidden = false;
  }

  async function refresh() {
    const data = await fetchSlots();
    if (!data) {
      // Leave whatever was rendered previously in place on transient failure;
      // only hide on initial failure when nothing has been rendered yet.
      if (!list.children.length) banner.hidden = true;
      return;
    }
    if (!rawResponseLogged) {
      console.log("[tipo-banner] raw slots response:", data);
      rawResponseLogged = true;
    }
    render(parseSlots(data));
  }

  refresh();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });

  window.setInterval(() => {
    if (document.visibilityState === "visible") refresh();
  }, REFRESH_INTERVAL_MS);
})();
